import React from "react";

/** Drop-in filter bar for the /contest/public-leagues grid. */

export const ASSET_OPTIONS = [
  { value: "any", label: "Any" },
  { value: "stocks", label: "Stocks" },
  { value: "crypto", label: "Crypto" },
  { value: "forex", label: "Forex" },
  { value: "mixed", label: "Mixed" },
];

export const DURATION_OPTIONS = [
  { value: "any", label: "Any duration" },
  { value: "short", label: "Under 1 week" },
  { value: "medium", label: "1–4 weeks" },
  { value: "long", label: "1–3 months" },
  { value: "epic", label: "3 months +" },
];

export const TYPE_OPTIONS = [
  { value: "any", label: "All types" },
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
  { value: "cash", label: "Cash prize" },
];

export const MEMBER_OPTIONS = [
  { value: "any", label: "Any size" },
  { value: "starting", label: "< 5 members" },
  { value: "small", label: "5–50 members" },
  { value: "medium", label: "51–100 members" },
  { value: "large", label: "101+ members" },
];

export const REWARD_OPTIONS = [
  { value: "any", label: "Any reward" },
  { value: "tier-1", label: "Tier 1" },
  { value: "tier-2", label: "Tier 2" },
  { value: "tier-3", label: "Tier 3" },
];

export const START_OPTIONS = [
  { value: "any", label: "Any start" },
  { value: "live", label: "Live now" },
  { value: "today", label: "Starts today" },
  { value: "this-week", label: "This week" },
  { value: "next-week", label: "Next week" },
  { value: "later", label: "Later" },
];

export const SORT_OPTIONS = [
  { value: "starting-soon", label: "Starting Soon" },
  { value: "most-members", label: "Most members" },
  { value: "newest", label: "Newest first" },
];

// Neutral template — every filter set to its "any" value. Pages that want a
// route-specific default (e.g. type: "public" on /contest/public-leagues)
// should spread this and override.
export const NEUTRAL_FILTERS = {
  asset: "any",
  duration: "any",
  type: "any",
  members: "any",
  rewards: "any",
  startTime: "any",
};

// Kept for backwards-compatibility with existing imports. Prefer NEUTRAL_FILTERS.
export const DEFAULT_FILTERS = NEUTRAL_FILTERS;

const FilterDropdown = ({ label, value, options, onChange, neutralValue = "any" }) => {
  const active = value && value !== neutralValue;
  return (
    <label className="relative inline-flex items-center">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className={`appearance-none rounded-full border px-3 py-1.5 pr-8 text-xs md:text-sm dm-sans focus:outline-none transition-colors cursor-pointer ${
          active
            ? "bg-[#E6F2F0] border-[#0F4146] text-[#0F4146] inter font-medium"
            : "bg-white border-[#E5E7EB] text-[#0F172A]"
        }`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.value === "any" ? label : o.label}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0F4146]"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M5.23 7.21a.75.75 0 011.06.02L10 11.585l3.71-4.355a.75.75 0 011.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
          clipRule="evenodd"
        />
      </svg>
    </label>
  );
};

const GameFiltersBar = ({ filters, onChange, sort, onSortChange, onClear, totalCount, filteredCount }) => {
  const activeCount = Object.values(filters).filter((v) => v && v !== "any").length;

  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-3 md:p-4">
      <div className="flex items-start gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 [&::-webkit-scrollbar]:hidden">
            <FilterDropdown
              label="Asset type"
              value={filters.asset}
              options={ASSET_OPTIONS}
              onChange={(v) => onChange({ ...filters, asset: v })}
            />
            <FilterDropdown
              label="Duration"
              value={filters.duration}
              options={DURATION_OPTIONS}
              onChange={(v) => onChange({ ...filters, duration: v })}
            />
            <FilterDropdown
              label="League type"
              value={filters.type}
              options={TYPE_OPTIONS}
              onChange={(v) => onChange({ ...filters, type: v })}
            />
            <FilterDropdown
              label="Members"
              value={filters.members}
              options={MEMBER_OPTIONS}
              onChange={(v) => onChange({ ...filters, members: v })}
            />
            <FilterDropdown
              label="Rewards"
              value={filters.rewards}
              options={REWARD_OPTIONS}
              onChange={(v) => onChange({ ...filters, rewards: v })}
            />
            <FilterDropdown
              label="Start time"
              value={filters.startTime}
              options={START_OPTIONS}
              onChange={(v) => onChange({ ...filters, startTime: v })}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <FilterDropdown
            label="Sort"
            value={sort}
            options={SORT_OPTIONS}
            onChange={onSortChange}
            neutralValue="starting-soon"
          />
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between text-xs dm-sans">
        <span className="text-[#66748C]">
          Showing {filteredCount} of {totalCount} leagues
        </span>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="text-[#0F4146] inter font-medium hover:underline"
          >
            Clear filters ({activeCount})
          </button>
        )}
      </div>
    </div>
  );
};

export default GameFiltersBar;
